// components/AIChatBot.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Send, X, Maximize2, Minimize2, MessageSquare } from 'lucide-react';
import { FormattedMessage } from './FormattedMessage';
import { N8N_AI_PROXY_URL } from '../constants';

const AI_MESSAGE_MAX_LENGTH = 500; // синхронно с bff/common.py:MAX_CHAT_QUESTION_LENGTH

// Историю чата сервер находит сам: вошедшего — по сессии, гостя — по httpOnly cookie,
// которую выдаёт BFF. Раньше фронт слал sessionId = user_<логин>_chat, и по чужому
// логину можно было прочитать чужую переписку.

export const AIChatBot = ({ currentAlerts, session }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  // Приветствие собирается при показе, а не при создании чата — иначе после смены языка
  // оставалось бы на старом. В английском интерфейсе — только по-английски, иначе kk + ru.
  const greeting = i18n.resolvedLanguage === 'en'
    ? t('AIChatBot.zdravstvuyte_ya_your_ai_analitik')
    : `${i18n.getFixedT('kk')('AIChatBot.zdravstvuyte_ya_your_ai_analitik')}\n\n${i18n.getFixedT('ru')('AIChatBot.zdravstvuyte_ya_your_ai_analitik')}`;
  const [messages, setMessages] = useState([{ role: 'assistant', greeting: true }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen, isMaximized]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > AI_MESSAGE_MAX_LENGTH) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await fetch(N8N_AI_PROXY_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: trimmed })
      });

      // 429 — лимит nginx на чат (12 сообщений в минуту с IP)
      if (response.status === 429) {
        setMessages(prev => [...prev, { role: 'assistant', content: t('AIChatBot.too_many_messages') }]);
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const botResponse = data.output || data.message || data.text || t('AIChatBot.ai_not_predostavil_reply');
      setMessages(prev => [...prev, { role: 'assistant', content: botResponse }]);
    } catch (err) {
      console.error("AI Assistant Error:", err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('AIChatBot.error_svyazi_ai_modulem_makesure', { value: err.message })
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className={`fixed z-[100] transition-all duration-300 ease-in-out flex flex-col items-end font-sans ${isMaximized && isOpen ? 'inset-6' : 'bottom-4 right-4 sm:bottom-8 sm:right-8'}`}>
      {isOpen && (
        <div className={`bg-brand-surface/95 backdrop-blur-2xl border border-gray-200 rounded-lg shadow-2xl flex flex-col overflow-hidden mb-4 animate-in slide-in-from-bottom-8 duration-300 ${isMaximized ? 'w-full h-full' : 'w-[calc(100vw-2rem)] sm:w-[420px] h-[70vh] sm:h-[600px] max-h-[600px]'}`}>
          {/* Шапка */}
          <div className="p-5 bg-brand-accent/30 border-b border-gray-100 flex justify-between items-center text-gray-900 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-brand-accent rounded-xs shadow-lg shadow-brand-accent/20"><Bot size={22} /></div>
              <div>
                <h4 className="text-sm font-black uppercase tracking-tight leading-none text-gray-900">ISV Assistant</h4>
                <p className="text-caption opacity-60 mt-1 font-bold italic tracking-wider">AI Security Analyst</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setIsMaximized(!isMaximized)} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all" title={isMaximized ? t('AIChatBot.svernut') : t('AIChatBot.uvelichit')}>
                {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button onClick={() => { setIsOpen(false); setIsMaximized(false); }} className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Сообщения */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scroll">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
                <div className={`max-w-[85%] p-4 rounded-sm text-small shadow-lg break-words overflow-hidden ${msg.role === 'user' ? 'bg-brand-accent text-gray-900 rounded-tr-none shadow-brand-accent/10' : 'bg-gray-100 text-gray-800 rounded-tl-none border border-gray-100 shadow-black/20'}`}>
                  <FormattedMessage content={msg.greeting ? greeting : msg.content} />
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100/40 p-4 rounded-sm animate-pulse flex gap-1.5 items-center">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce" />
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Инпут */}
          <form onSubmit={handleSendMessage} className="p-5 bg-gray-50/40 border-t border-gray-100">
            <div className={`flex gap-3 ${isMaximized ? 'max-w-4xl mx-auto' : ''}`}>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value.slice(0, AI_MESSAGE_MAX_LENGTH))}
                  maxLength={AI_MESSAGE_MAX_LENGTH}
                  placeholder={t('AIChatBot.sprosite_ob_threat_or_soc')}
                  className="w-full bg-gray-50/50 border border-gray-200 px-5 py-4 rounded-xs text-sm outline-none focus:ring-2 focus:ring-brand-accent/50 text-gray-900 placeholder:text-gray-500 transition-all"
                />
                {input.length > AI_MESSAGE_MAX_LENGTH * 0.8 && (
                  <span className="absolute right-4 -bottom-5 text-[10px] text-gray-400">
                    {input.length}/{AI_MESSAGE_MAX_LENGTH}
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={isTyping}
                className="bg-brand-accent p-4 rounded-xs text-gray-900 hover:bg-brand-accent-hover transition-all active:scale-95 shadow-lg shadow-brand-accent/20 disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FAB кнопка */}
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="p-5 rounded-full bg-brand-accent text-gray-900 shadow-2xl shadow-brand-accent/40 transition-all duration-500 hover:scale-110 active:scale-90 relative group">
          <MessageSquare size={28} />
          <span className="absolute -top-1 -right-1 flex h-5 w-5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-brand-accent border-2 border-brand-bg flex items-center justify-center text-caption font-black">AI</span>
          </span>
        </button>
      )}
    </div>
  );
};
